import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrackChanges as TrackChangesIcon,
  FitnessCenter as FitnessIcon,
  MonitorWeight as WeightIcon,
  Straighten as StraightenIcon,
} from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '20px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  border: 'none',
  marginBottom: theme.spacing(3),
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
}));

// Mapping for metric icons and colors
const getMetricStyle = (metricName) => {
  const name = metricName?.toLowerCase() || '';
  if (name.includes('peso')) return { icon: '⚖️', color: '#e0e7ff', textColor: '#667eea' };
  if (name.includes('imc') || name.includes('masa')) return { icon: '📊', color: '#dbeafe', textColor: '#3b82f6' };
  if (name.includes('grasa')) return { icon: '💪', color: '#fef3c7', textColor: '#f59e0b' };
  if (name.includes('cintura') || name.includes('circunferencia')) return { icon: '📏', color: '#fce7f3', textColor: '#ec4899' };
  if (name.includes('músculo') || name.includes('muscular')) return { icon: '🏋️', color: '#dcfce7', textColor: '#22c55e' };
  return { icon: '📈', color: '#f1f5f9', textColor: '#64748b' };
};

const TrackingGoals = ({ userData, handleInputChange, allMetrics = [] }) => {
  const [wantsTracking, setWantsTracking] = useState(userData.wantsTracking);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const [userMetricsResponse, defaultMetricsResponse] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL}/metrics/user-metrics`, {
          headers: { Authorization: `Bearer ${userData.authToken}` },
        }),
        fetch(`${import.meta.env.VITE_API_URL}/metrics/metric-types`, {
          headers: { Authorization: `Bearer ${userData.authToken}` },
        })
      ]);

      const userMetrics = await userMetricsResponse.json();
      const defaultMetrics = await defaultMetricsResponse.json();

      // Only show default metrics (hide custom metrics)
      const filteredMetrics = defaultMetrics.filter(metric => metric.isDefault).map(metric => ({
        ...metric,
        isTracking: false
      }));

      // Compare with user-metrics and update isTracking
      const combinedMetrics = filteredMetrics.map(metric => {
        const userMetric = userMetrics.find(um => um.metricType._id === metric._id);
        return {
          ...metric,
          isTracking: userMetric ? userMetric.isTracking : false
        };
      });

      setMetrics(combinedMetrics);
      setWantsTracking(userData.wantsTracking);
      handleInputChange('wantsTracking', userData.wantsTracking);
      handleInputChange('allMetrics', combinedMetrics);
      updateGlobalMetrics(combinedMetrics);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateGlobalMetrics = (currentMetrics) => {
    const trackedMetrics = currentMetrics
      .filter(metric => metric.isTracking)
      .map(metric => metric._id);

    handleInputChange('trackedMetrics', trackedMetrics);
    handleInputChange('customMetrics', []); // No custom metrics for now
  };

  const handleWantsTrackingChange = (event) => {
    setWantsTracking(event.target.checked);
    handleInputChange('wantsTracking', event.target.checked);
    if (!event.target.checked) {
      const updatedMetrics = metrics.map(metric => ({ ...metric, isTracking: false }));
      setMetrics(updatedMetrics);
      updateGlobalMetrics(updatedMetrics);
    }
  };

  const handleMetricToggle = async (metricId) => {
    const updatedMetrics = metrics.map(metric =>
      metric._id === metricId
        ? { ...metric, isTracking: !metric.isTracking }
        : metric
    );
    setMetrics(updatedMetrics);
    updateGlobalMetrics(updatedMetrics);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Main Toggle Card */}
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconBox sx={{ bgcolor: '#e0e7ff' }}>
                <TrackChangesIcon sx={{ color: '#667eea' }} />
              </IconBox>
              <Box>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  Seguimiento de métricas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Activa para registrar tu evolución corporal
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={wantsTracking}
              onChange={handleWantsTrackingChange}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#667eea',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#667eea',
                  },
                },
              }}
            />
          </Box>
        </CardContent>
      </StyledCard>

      {/* Metrics Selection */}
      {wantsTracking && (
        <StyledCard>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="600" color="#1e293b" sx={{ mb: 1 }}>
              Selecciona las métricas a seguir
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Elige qué datos quieres registrar periódicamente
            </Typography>

            <Grid container spacing={2}>
              {metrics.map((metric) => {
                const style = getMetricStyle(metric.name);
                return (
                  <Grid item xs={12} sm={6} key={metric._id}>
                    <Box
                      onClick={() => handleMetricToggle(metric._id)}
                      sx={{
                        p: 2.5,
                        borderRadius: '16px',
                        border: '2px solid',
                        borderColor: metric.isTracking ? '#667eea' : '#e2e8f0',
                        bgcolor: metric.isTracking ? '#e0e7ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '&:hover': {
                          borderColor: '#667eea',
                          transform: 'scale(1.01)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            bgcolor: style.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            fontSize: '1.3rem',
                          }}
                        >
                          {style.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight={metric.isTracking ? 700 : 500}
                            color={metric.isTracking ? '#667eea' : '#1e293b'}
                          >
                            {metric.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {metric.unit}
                          </Typography>
                        </Box>
                      </Box>
                      <Switch
                        checked={metric.isTracking}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#667eea',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#667eea',
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </StyledCard>
      )}
    </Box>
  );
};

export default TrackingGoals;